import Fuse from 'fuse.js'
import { db } from '@/utils/Firebase'

// Cache en memoria. Key: coachId | 'app'. Se resetea al recargar.
const cache = new Map() // key -> { exercises, fuse }
// Cache del Fuse del pool combinado (sin filtro de types), keyed por coachId|companyId|includeApp.
const mergedCache = new Map() // mergeKey -> fuse

const FUSE_OPTIONS = {
    keys: ['name'],
    threshold: 0.35,
    ignoreLocation: true,
    minMatchCharLength: 2,
}

// key formats:
//   'app'                    -> where owner == 'app'
//   'company:<companyId>'    -> where companyId == <companyId>
//   '<coachId>'              -> where coachId == <coachId>
async function loadByKey(key) {
    if (cache.has(key)) return cache.get(key).exercises

    let query
    if (key === 'app') {
        query = db.collection('exercises').where('owner', '==', 'app')
    } else if (key.startsWith('company:')) {
        const companyId = key.slice('company:'.length)
        query = db.collection('exercises').where('companyId', '==', companyId)
    } else {
        query = db.collection('exercises').where('coachId', '==', key)
    }

    const snap = await query.get()
    const exercises = snap.docs
        .map(d => ({ objectID: d.id, ...d.data() }))
        .filter(ex => !ex.hide)

    const fuse = new Fuse(exercises, FUSE_OPTIONS)
    cache.set(key, { exercises, fuse })
    return exercises
}

/**
 * Carga al login: ejercicios del coach actual + biblioteca publica (owner=app).
 * Se llama desde store.initUser. Tolerante a errores: si falla, los dialogos
 * siguen funcionando porque cargan on-demand (loadByKey).
 */
export async function preloadExerciseLibrary(currentUser) {
    if (!currentUser || !currentUser.uid) return
    try {
        await Promise.all([
            loadByKey(currentUser.uid),
            loadByKey('app'),
        ])
    } catch (e) {
        console.warn('[ExerciseLibrary] preload fallo, se cargara on-demand:', e?.message)
    }
}

/**
 * Busca ejercicios filtrando por coachId + biblioteca publica + (opcional) companyId.
 * Reemplaza el patron Algolia `(coachId:X OR companyId:Y OR owner:app) AND types:...`.
 *
 * @param {object} opts
 * @param {string} opts.coachId - dueño de los ejercicios a incluir
 * @param {string} [opts.companyId] - si coach_company/company, incluye sus ejercicios
 * @param {string} [opts.query=''] - texto del buscador
 * @param {Array}  [opts.types=[]] - tipos para filtrar, formato [{name: 'fuerza'}, ...]
 * @param {boolean}[opts.includeApp=true] - incluir biblioteca publica
 * @param {number} [opts.limit=50] - tope de resultados
 * @returns {Promise<Array>} hits con shape similar a los de Algolia
 */
export async function searchExercises(opts) {
    const {
        coachId,
        companyId = null,
        query = '',
        types = [],
        includeApp = true,
        limit = 50,
    } = opts || {}

    if (!coachId) return []

    const loaders = [loadByKey(coachId)]
    if (includeApp) loaders.push(loadByKey('app'))
    if (companyId) loaders.push(loadByKey(`company:${companyId}`))

    const lists = await Promise.all(loaders)
    // Dedupe por objectID (un ejercicio puede aparecer en >1 lista, ej coachId + companyId)
    const seen = new Set()
    let pool = []
    for (const list of lists) {
        for (const ex of list) {
            if (!seen.has(ex.objectID)) {
                seen.add(ex.objectID)
                pool.push(ex)
            }
        }
    }

    const hasTypes = Array.isArray(types) && types.length > 0
    if (hasTypes) {
        const typeNames = types.map(t => t.name)
        pool = pool.filter(ex =>
            Array.isArray(ex.types) && ex.types.some(t => typeNames.includes(t))
        )
    }

    const q = (query || '').trim()
    if (!q) return pool.slice(0, limit)

    let fuse
    if (hasTypes) {
        // Con filtro de types el pool cambia: indice nuevo, no cacheable.
        fuse = new Fuse(pool, FUSE_OPTIONS)
    } else {
        // Sin filtro de types el pool es estable: reusar el Fuse cacheado entre teclas.
        const mergeKey = `${coachId}|${companyId || ''}|${includeApp ? 1 : 0}`
        fuse = mergedCache.get(mergeKey)
        if (!fuse) {
            fuse = new Fuse(pool, FUSE_OPTIONS)
            mergedCache.set(mergeKey, fuse)
        }
    }
    return fuse.search(q, { limit }).map(r => r.item)
}

/**
 * Invalida el cache de un coach (o de todo). Llamar despues de CRUD de ejercicios.
 * El proximo search vuelve a leer de Firestore.
 */
export function invalidateExerciseCache(key) {
    if (key) cache.delete(key)
    else cache.clear()
    // El merged-cache se arma de varias keys: ante cualquier CRUD se limpia entero.
    mergedCache.clear()
}
